import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Employee } from '../types';
import {
  RiUserAddLine, RiToggleLine, RiToggleFill, RiDeleteBinLine,
  RiShieldUserLine, RiUserLine,
} from 'react-icons/ri';

const inputClass =
  'w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

const Avatar: React.FC<{ prenom: string; nom: string; actif: boolean }> = ({ prenom, nom, actif }) => {
  const initials = `${prenom[0] ?? ''}${nom[0] ?? ''}`.toUpperCase();
  return (
    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
      actif ? 'bg-brand-100 text-brand-700' : 'bg-gray-100 text-gray-400'
    }`}>
      {initials}
    </div>
  );
};

const AdminEmployees: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [newEmployee, setNewEmployee] = useState({
    username: '', password: '', prenom: '', nom: '', role: 'employe',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await api.get('/employees/');
      setEmployees(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const showFeedback = (msg: string, isError = false) => {
    if (isError) setError(msg);
    else setSuccess(msg);
    setTimeout(() => { setError(''); setSuccess(''); }, 3000);
  };

  const handleAddEmployee = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/employees/', newEmployee);
      setNewEmployee({ username: '', password: '', prenom: '', nom: '', role: 'employe' });
      fetchEmployees();
      showFeedback('Employé ajouté avec succès.');
    } catch {
      showFeedback("Erreur lors de l'ajout. Le nom d'utilisateur existe peut-être déjà.", true);
    }
  };

  const handleToggleActif = async (employee: Employee) => {
    try {
      await api.put(`/employees/${employee.id}`, { actif: !employee.actif });
      fetchEmployees();
    } catch {
      console.error("Erreur lors de la modification de l'employé");
    }
  };

  const handleDeleteEmployee = async (id: number) => {
    if (window.confirm('Supprimer cet employé définitivement ?')) {
      try {
        await api.delete(`/employees/${id}`);
        fetchEmployees();
        showFeedback('Employé supprimé.');
      } catch {
        showFeedback("Erreur lors de la suppression.", true);
      }
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Employés</h1>
        <p className="text-sm text-gray-500 mt-1">Gérez les comptes de votre équipe</p>
      </div>

      {/* Feedback banners */}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
          {success}
        </div>
      )}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Add form */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <RiUserAddLine className="text-brand-500" />
              Nouvel employé
            </h2>
            <form onSubmit={handleAddEmployee} className="space-y-3">
              <div>
                <label className={labelClass}>Prénom</label>
                <input type="text" placeholder="Marie" value={newEmployee.prenom}
                  onChange={e => setNewEmployee({ ...newEmployee, prenom: e.target.value })}
                  className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Nom</label>
                <input type="text" placeholder="Dupont" value={newEmployee.nom}
                  onChange={e => setNewEmployee({ ...newEmployee, nom: e.target.value })}
                  className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Nom d'utilisateur</label>
                <input type="text" placeholder="marie.dupont" value={newEmployee.username}
                  onChange={e => setNewEmployee({ ...newEmployee, username: e.target.value })}
                  className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Mot de passe</label>
                <input type="password" placeholder="••••••••" value={newEmployee.password}
                  onChange={e => setNewEmployee({ ...newEmployee, password: e.target.value })}
                  className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Rôle</label>
                <select value={newEmployee.role}
                  onChange={e => setNewEmployee({ ...newEmployee, role: e.target.value })}
                  className={inputClass}>
                  <option value="employe">Employé</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              <button type="submit"
                className="w-full bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium py-2.5 rounded-lg transition mt-1">
                Créer le compte
              </button>
            </form>
          </div>
        </div>

        {/* Employee list */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-800">
                Équipe
                <span className="ml-2 text-xs font-normal text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                  {employees.length}
                </span>
              </h2>
            </div>
            {employees.length === 0 ? (
              <p className="px-5 py-8 text-center text-gray-400 text-sm">Aucun employé enregistré</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {employees.map(emp => (
                  <li key={emp.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors">
                    <Avatar prenom={emp.prenom} nom={emp.nom} actif={emp.actif} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm text-gray-900">
                          {emp.prenom} {emp.nom}
                        </p>
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                          emp.role === 'admin'
                            ? 'bg-brand-50 text-brand-600'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {emp.role === 'admin'
                            ? <><RiShieldUserLine className="text-xs" />Admin</>
                            : <><RiUserLine className="text-xs" />Employé</>
                          }
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          emp.actif
                            ? 'bg-green-50 text-green-600'
                            : 'bg-gray-100 text-gray-400'
                        }`}>
                          {emp.actif ? 'Actif' : 'Inactif'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">@{emp.username}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleToggleActif(emp)}
                        title={emp.actif ? 'Désactiver' : 'Activer'}
                        className={`p-1.5 rounded-lg transition text-lg ${
                          emp.actif
                            ? 'text-orange-400 hover:text-orange-600 hover:bg-orange-50'
                            : 'text-gray-300 hover:text-green-500 hover:bg-green-50'
                        }`}
                      >
                        {emp.actif ? <RiToggleFill /> : <RiToggleLine />}
                      </button>
                      <button
                        onClick={() => handleDeleteEmployee(emp.id)}
                        title="Supprimer"
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                      >
                        <RiDeleteBinLine />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEmployees;
